<template>
  <v-dialog :value="value" @input="v => { if (!v) close(); }" max-width="640" scrollable :persistent="saving"
    :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card class="sed" dir="rtl">
      <!-- ===== header ===== -->
      <header class="sed-head">
        <h2 class="sed-title">{{ isNew ? 'סקר חדש' : `עריכת סקר #${form.integer_id}` }}</h2>
        <v-btn icon aria-label="סגירה" :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </header>

      <v-card-text class="sed-body">
        <!-- ===== question ===== -->
        <section class="sed-section">
          <h3 class="sed-h">השאלה</h3>
          <v-text-field v-model="form.question" label="שם הסקר בממשק" placeholder="לדוגמה: איזו מנה להגיש בשבת?"
            hint="רק לזיהוי בממשק. המתקשרים שומעים את ההקלטה." persistent-hint outlined dense maxlength="120" />

          <!-- new survey: the recording the callers hear -->
          <div v-if="isNew" class="sed-audio">
            <input ref="audioInput" type="file" accept="audio/wav,audio/mpeg,audio/mp3,.wav,.mp3" class="sed-audio__input"
              id="sed-audio-input" @change="onAudioSelected">
            <label v-if="!audioFile" for="sed-audio-input" class="sed-drop" :class="{ 'is-over': dragOver, 'is-error': showErrors && !audioFile }"
              @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false" @drop.prevent="onAudioDropped">
              <v-icon color="#0d2c6d">mdi-microphone-outline</v-icon>
              <span class="sed-drop__title">הקלטת השאלה</span>
              <span class="sed-drop__hint">גוררים לכאן קובץ, או לוחצים לבחירה. WAV או MP3, עד 10MB.</span>
            </label>
            <div v-else class="sed-file">
              <v-btn icon class="sed-file__play" :aria-label="isPlaying ? 'עצירת ההשמעה' : 'השמעה'" @click="togglePreview">
                <v-icon>{{ isPlaying ? 'mdi-stop' : 'mdi-play' }}</v-icon>
              </v-btn>
              <div class="sed-file__text">
                <span class="sed-file__name">{{ audioFile.name }}</span>
                <span class="sed-file__size">{{ formatFileSize(audioFile.size) }}</span>
              </div>
              <v-btn text small color="#0d2c6d" @click="$refs.audioInput.click()">החלפה</v-btn>
              <v-btn text small color="#c62828" @click="removeAudio">הסרה</v-btn>
            </div>
            <p v-if="audioError" class="sed-error-text">{{ audioError }}</p>
            <p v-else-if="showErrors && !audioFile" class="sed-error-text">צריך להעלות את הקלטת השאלה.</p>
          </div>
          <p v-else class="sed-note">
            <v-icon small color="#6b7690">mdi-microphone-outline</v-icon>
            הקלטת השאלה נשארת כמו שהיא. כדי להחליף הקלטה, משכפלים את הסקר או יוצרים סקר חדש.
          </p>
        </section>

        <!-- ===== answer keys: a phone keypad ===== -->
        <section class="sed-section">
          <div class="sed-h-row">
            <h3 class="sed-h">מקשי תשובה</h3>
            <span class="sed-h-aside">{{ keysSummary }}</span>
          </div>
          <!-- כשכבר יש הצבעות: רק המקשים שנבחרו, בשורה אחת -->
          <div v-if="keysLocked" class="sed-keys-locked" aria-label="מקשי התשובה של הסקר">
            <span v-for="k in form.optionKeys" :key="k" class="sed-key sed-key--chip is-on">{{ k }}</span>
          </div>
          <div v-else class="sed-keypad" role="group" aria-label="בחירת מקשי תשובה">
            <template v-for="(d, i) in keypad">
              <button v-if="d !== null" :key="'k' + i" type="button" class="sed-key"
                :class="{ 'is-on': form.optionKeys.includes(d) }" :aria-pressed="form.optionKeys.includes(d) ? 'true' : 'false'"
                :disabled="keysLocked" @click="toggleKey(d)">
                {{ d }}
              </button>
              <span v-else :key="'g' + i" class="sed-key sed-key--blank" aria-hidden="true"></span>
            </template>
          </div>
          <p v-if="keysLocked" class="sed-note">
            <v-icon small color="#6b7690">mdi-lock-outline</v-icon>
            כבר יש הצבעות בסקר, ולכן אי אפשר לשנות את המקשים.
          </p>
          <p v-else-if="showErrors && form.optionKeys.length < 2" class="sed-error-text">בוחרים לפחות 2 מקשים.</p>
        </section>

        <!-- ===== voting rules ===== -->
        <section class="sed-section">
          <h3 class="sed-h">כללי ההצבעה</h3>
          <div class="sed-rules">
            <div class="sed-rule">
              <div class="sed-rule__text">
                <span class="sed-rule__title">יותר מהצבעה אחת</span>
                <span class="sed-rule__hint">כל מתקשר יכול להצביע כמה פעמים, למקשים שונים.</span>
              </div>
              <v-switch v-model="form.allowMultipleVotes" inset hide-details color="#0d2c6d" class="sed-switch" aria-label="יותר מהצבעה אחת" />
            </div>
            <div v-if="form.allowMultipleVotes" class="sed-rule sed-rule--sub">
              <span class="sed-rule__title">עד כמה הצבעות למתקשר</span>
              <div class="sed-stepper" role="group" aria-label="מספר הצבעות למתקשר">
                <v-btn icon small :disabled="form.maxVotesPerUser <= 2" aria-label="פחות" @click="form.maxVotesPerUser--"><v-icon small>mdi-minus</v-icon></v-btn>
                <span class="sed-stepper__value">{{ form.maxVotesPerUser }}</span>
                <v-btn icon small :disabled="form.maxVotesPerUser >= maxVotesLimit" aria-label="יותר" @click="form.maxVotesPerUser++"><v-icon small>mdi-plus</v-icon></v-btn>
              </div>
            </div>

            <div class="sed-rule">
              <div class="sed-rule__text">
                <span class="sed-rule__title">הצבעה אנונימית</span>
                <span class="sed-rule__hint">{{ anonymousLocked ? 'כבר יש הצבעות בסקר, ולכן אי אפשר לשנות את זה.' : 'מספרי הטלפון של המצביעים לא נשמרים.' }}</span>
              </div>
              <v-switch v-model="form.anonymousVoting" inset hide-details color="#0d2c6d" class="sed-switch"
                :disabled="anonymousLocked" aria-label="הצבעה אנונימית" />
            </div>

            <div class="sed-rule">
              <div class="sed-rule__text">
                <span class="sed-rule__title">תוצאות בזמן אמת</span>
                <span class="sed-rule__hint">מתקשרים יכולים לשמוע את התוצאות לפני שהסקר נסגר.</span>
              </div>
              <v-switch v-model="form.allowResultsDuringVote" inset hide-details color="#0d2c6d" class="sed-switch" aria-label="תוצאות בזמן אמת" />
            </div>

            <div class="sed-rule">
              <div class="sed-rule__text">
                <span class="sed-rule__title">שינוי הצבעה</span>
                <span class="sed-rule__hint">מתקשרים יכולים לבטל את ההצבעה ולהצביע מחדש, כל עוד הסקר פתוח.</span>
              </div>
              <v-switch v-model="form.allowVoteChange" inset hide-details color="#0d2c6d" class="sed-switch" aria-label="שינוי הצבעה" />
            </div>
          </div>
        </section>

        <!-- ===== timing ===== -->
        <section class="sed-section">
          <h3 class="sed-h">מועדים</h3>

          <template v-if="isNew">
            <span class="sed-label">פתיחת הסקר</span>
            <div class="sed-seg" role="radiogroup" aria-label="פתיחת הסקר">
              <button v-for="o in scheduleOptions" :key="o.value" type="button" role="radio" class="sed-seg__btn"
                :class="{ 'is-active': form.scheduleType === o.value }" :aria-checked="form.scheduleType === o.value ? 'true' : 'false'"
                @click="form.scheduleType = o.value">{{ o.text }}</button>
            </div>
            <div v-if="form.scheduleType === 'scheduled'" class="sed-row2">
              <v-text-field v-model="form.scheduleDate" type="date" label="תאריך" outlined dense hide-details :min="today"
                :error="showErrors && !scheduleValid" />
              <v-text-field v-model="form.scheduleTime" type="time" label="שעה" outlined dense hide-details
                :error="showErrors && !scheduleValid" />
            </div>
            <p v-if="showErrors && form.scheduleType === 'scheduled' && !scheduleValid" class="sed-error-text">בוחרים תאריך ושעה עתידיים.</p>
          </template>

          <span class="sed-label">סגירת הסקר</span>
          <div class="sed-seg" role="radiogroup" aria-label="סגירת הסקר">
            <button v-for="o in durationOptions" :key="o.value" type="button" role="radio" class="sed-seg__btn"
              :class="{ 'is-active': form.durationType === o.value }" :aria-checked="form.durationType === o.value ? 'true' : 'false'"
              @click="form.durationType = o.value">{{ o.text }}</button>
          </div>
          <div v-if="form.durationType === 'hours' || form.durationType === 'days'" class="sed-duration">
            <span>למשך</span>
            <v-text-field v-model.number="form.durationAmount" type="number" min="1" :max="form.durationType === 'hours' ? 720 : 365"
              outlined dense hide-details class="sed-duration__input" aria-label="משך" />
            <span>{{ form.durationType === 'hours' ? 'שעות' : 'ימים' }}</span>
          </div>
          <p class="sed-preview">{{ closingPreview }}</p>

          <div v-if="isNew" class="sed-rule sed-rule--flat">
            <div class="sed-rule__text">
              <span class="sed-rule__title">צינתוק לחברים</span>
              <span class="sed-rule__hint">כשהסקר נפתח, כל החברים מקבלים צינתוק.</span>
            </div>
            <v-switch v-model="form.sendCalls" inset hide-details color="#0d2c6d" class="sed-switch" aria-label="צינתוק לחברים" />
          </div>
        </section>
      </v-card-text>

      <!-- ===== footer ===== -->
      <footer class="sed-foot">
        <p v-if="saveError" class="sed-foot__error" role="alert">
          <v-icon small color="#c62828">mdi-alert-circle-outline</v-icon>
          {{ saveError }}
        </p>
        <div class="sed-foot__actions">
          <v-btn text :disabled="saving" @click="close">ביטול</v-btn>
          <v-btn color="#0d2c6d" dark depressed :loading="saving" class="sed-save" @click="save">
            {{ isNew ? 'יצירת הסקר' : 'שמירת השינויים' }}
          </v-btn>
        </div>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import axios from 'axios'

const MAX_AUDIO_BYTES = 10 * 1024 * 1024;

const emptyForm = () => ({
  question: '',
  optionKeys: [],
  allowMultipleVotes: false,
  maxVotesPerUser: 2,
  anonymousVoting: false,
  allowResultsDuringVote: true,
  allowVoteChange: true,
  scheduleType: 'now',
  scheduleDate: '',
  scheduleTime: '',
  durationType: 'unlimited',
  durationAmount: 1,
  sendCalls: false,
  integer_id: null
});

export default {
  name: 'SurveyEditDialog',
  props: {
    value: Boolean,
    survey: Object,
    isNew: Boolean
  },
  data: () => ({
    saving: false,
    showErrors: false,
    saveError: '',
    audioError: '',
    dragOver: false,
    audioFile: null,
    audioPreviewUrl: null,
    audioPlayer: null,
    isPlaying: false,
    form: emptyForm(),
    // phone keypad layout: 1-9, then 0 in the middle of the last row
    keypad: [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, null],
    scheduleOptions: [
      { value: 'now', text: 'מיד' },
      { value: 'scheduled', text: 'במועד שאבחר' },
    ],
  }),
  computed: {
    hasVotes() {
      return !this.isNew && !!this.survey && (this.survey.votesCount || 0) > 0;
    },
    keysLocked() { return this.hasVotes; },
    anonymousLocked() { return this.hasVotes; },
    maxVotesLimit() {
      return Math.max(2, this.form.optionKeys.length || 2);
    },
    keysSummary() {
      const n = this.form.optionKeys.length;
      if (!n) return 'עוד לא נבחרו מקשים';
      return `נבחרו ${n}: ${[...this.form.optionKeys].sort((a, b) => a - b).join(', ')}`;
    },
    durationOptions() {
      const opts = [];
      if (!this.isNew && this.survey && this.survey.endDate) opts.push({ value: 'keep', text: 'ללא שינוי' });
      opts.push({ value: 'unlimited', text: 'רק ידנית' }, { value: 'hours', text: 'אחרי שעות' }, { value: 'days', text: 'אחרי ימים' });
      return opts;
    },
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    scheduleStart() {
      if (this.form.scheduleType !== 'scheduled' || !this.form.scheduleDate || !this.form.scheduleTime) return null;
      const d = new Date(`${this.form.scheduleDate}T${this.form.scheduleTime}`);
      return isNaN(d) ? null : d;
    },
    scheduleValid() {
      if (this.form.scheduleType !== 'scheduled') return true;
      return !!this.scheduleStart && this.scheduleStart > new Date();
    },
    closingPreview() {
      const t = this.form.durationType;
      if (t === 'keep') {
        const past = new Date(this.survey.endDate) <= new Date();
        return `הסקר ${past ? 'נסגר' : 'ייסגר'} ${this.formatWhen(this.survey.endDate)}.`;
      }
      if (t === 'unlimited') return 'הסקר נשאר פתוח עד שסוגרים אותו בטלפון או כאן.';
      const amount = Math.max(1, Number(this.form.durationAmount) || 1);
      // בסקר מתוזמן השרת סופר את המשך ממועד הפתיחה
      const end = new Date((this.isNew && this.scheduleStart) ? this.scheduleStart : Date.now());
      if (t === 'hours') end.setHours(end.getHours() + amount);
      else end.setDate(end.getDate() + amount);
      return `הסקר ייסגר ${this.formatWhen(end)}.`;
    },
    formValid() {
      return this.form.optionKeys.length >= 2 && (!this.isNew || !!this.audioFile) && this.scheduleValid;
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.initForm();
        this.audioFile = null;
        this.audioPreviewUrl = null;
        this.isPlaying = false;
        this.showErrors = false;
        this.saveError = '';
        this.audioError = '';
        if (this.audioPlayer) {
          this.audioPlayer.pause();
          this.audioPlayer = null;
        }
      }
    },
    'form.optionKeys'() {
      if (this.form.maxVotesPerUser > this.maxVotesLimit) this.form.maxVotesPerUser = this.maxVotesLimit;
    },
  },
  methods: {
    initForm() {
      if (this.isNew || !this.survey) {
        this.form = emptyForm();
      } else {
        const s = this.survey;
        this.form = {
          ...emptyForm(),
          _id: s._id,
          question: s.question || '',
          optionKeys: s.options ? s.options.map(o => o.key) : [],
          allowMultipleVotes: s.allowMultipleVotes || false,
          maxVotesPerUser: Math.max(2, s.maxVotesPerUser || 2),
          anonymousVoting: s.anonymousVoting || false,
          allowResultsDuringVote: s.allowResultsDuringVote !== false,
          allowVoteChange: s.allowVoteChange !== false,
          // מועד סיום קיים נשאר כמו שהוא, אלא אם בוחרים במפורש משך חדש
          durationType: s.endDate ? 'keep' : 'unlimited',
          integer_id: s.integer_id
        };
      }
    },

    formatWhen(date) {
      const d = new Date(date);
      const day = d.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'numeric' });
      const time = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
      return `${day} בשעה ${time}`;
    },

    // === Audio handling ===
    setAudio(file) {
      this.audioError = '';
      if (!file) return;
      if (!/\.(wav|mp3)$/i.test(file.name)) {
        this.audioError = 'אפשר להעלות רק קובץ WAV או MP3.';
        return;
      }
      if (file.size > MAX_AUDIO_BYTES) {
        this.audioError = 'הקובץ גדול מ-10MB. מקצרים את ההקלטה או שומרים אותה כ-MP3.';
        return;
      }
      this.removeAudio();
      this.audioFile = file;
      this.audioPreviewUrl = URL.createObjectURL(file);
    },
    onAudioSelected(event) {
      this.setAudio(event.target.files[0]);
      event.target.value = '';
    },
    onAudioDropped(event) {
      this.dragOver = false;
      this.setAudio(event.dataTransfer.files[0]);
    },

    togglePreview() {
      if (this.isPlaying) {
        if (this.audioPlayer) {
          this.audioPlayer.pause();
          this.audioPlayer.currentTime = 0;
        }
        this.isPlaying = false;
      } else {
        this.audioPlayer = new Audio(this.audioPreviewUrl);
        this.audioPlayer.play();
        this.isPlaying = true;
        this.audioPlayer.addEventListener('ended', () => {
          this.isPlaying = false;
        });
      }
    },

    removeAudio() {
      if (this.audioPlayer) {
        this.audioPlayer.pause();
        this.audioPlayer = null;
      }
      this.isPlaying = false;
      this.audioFile = null;
      if (this.audioPreviewUrl) {
        URL.revokeObjectURL(this.audioPreviewUrl);
        this.audioPreviewUrl = null;
      }
      if (this.$refs.audioInput) {
        this.$refs.audioInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    // === Options ===
    toggleKey(n) {
      if (this.keysLocked) return;
      if (this.form.optionKeys.includes(n)) {
        this.form.optionKeys = this.form.optionKeys.filter(k => k !== n);
      } else {
        this.form.optionKeys = [...this.form.optionKeys, n].sort((a, b) => a - b);
      }
    },

    close() {
      if (this.saving) return;
      this.removeAudio();
      this.$emit('input', false);
    },

    async save() {
      this.saveError = '';
      if (!this.formValid) {
        this.showErrors = true;
        return;
      }

      try {
        this.saving = true;
        const token = localStorage.getItem("token");

        // Build payload
        const payload = {
          question: this.form.question,
          options: this.form.optionKeys.map(k => ({ key: k, text: '' })),
          allowMultipleVotes: this.form.allowMultipleVotes,
          maxVotesPerUser: this.form.allowMultipleVotes ? (this.form.maxVotesPerUser || 2) : 1,
          anonymousVoting: this.form.anonymousVoting,
          allowResultsDuringVote: this.form.allowResultsDuringVote,
          allowVoteChange: this.form.allowVoteChange,
        };

        // Duration ('keep' = endDate לא נשלח, והשרת משאיר את הקיים)
        if (this.form.durationType === 'unlimited') {
          payload.endDate = null;
        } else if (this.form.durationType !== 'keep') {
          const amount = Math.max(1, Number(this.form.durationAmount) || 1);
          const now = new Date();
          if (this.form.durationType === 'hours') {
            now.setHours(now.getHours() + amount);
          } else {
            now.setDate(now.getDate() + amount);
          }
          payload.endDate = now.toISOString();
        }

        if (this.isNew) {
          // Scheduling
          if (this.scheduleStart) {
            payload.scheduleDate = this.scheduleStart.toISOString();
          }
          payload.sendCalls = this.form.sendCalls;

          // Use FormData to include audio file
          const formData = new FormData();
          formData.append('payload', JSON.stringify(payload));
          if (this.audioFile) {
            formData.append('audio', this.audioFile);
          }

          const api = process.env.VUE_APP_BASE_URL + '/surveys/create';
          const response = await axios.post(api, formData, {
            headers: {
              'Authorization': 'Bearer ' + token,
              'Content-Type': 'multipart/form-data'
            }
          });

          this.saving = false;
          if (response.status === 200) {
            this.$emit('saved');
            this.close();
          } else {
            this.saveError = (response.data && response.data.message) || 'הסקר לא נוצר. נסו שוב.';
          }
        } else {
          // Update - no audio change
          payload.surveyId = this.form._id;
          const api = process.env.VUE_APP_BASE_URL + '/surveys/update';
          const res = await fetch(api, ApiServices.requestOptions("PUT", JSON.stringify(payload), token));

          this.saving = false;
          if (res.status === 200) {
            this.$emit('saved');
            this.close();
          } else {
            const data = await res.json().catch(() => ({}));
            this.saveError = data.message || 'השינויים לא נשמרו. נסו שוב.';
          }
        }
      } catch (err) {
        this.saving = false;
        const msg = err.response && err.response.data && err.response.data.message;
        this.saveError = msg || ('לא הצלחנו לשמור: ' + err.message);
      }
    }
  },
  beforeDestroy() {
    this.removeAudio();
  }
}
</script>

<style scoped>
.sed {
  --ink: #0d2c6d;
  --ink-soft: #3d5488;
  --gold: #ffb91a;
  --line: #e3e8f2;
  --muted: #6b7690;
  --danger: #c62828;
  font-family: 'Heebo', sans-serif;
  color: var(--ink);
  border-radius: 18px !important;
}

.sed-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--line);
}
.sed-title { font-size: 21px; font-weight: 800; margin: 0; }

.sed-body { padding: 6px 24px 8px !important; color: var(--ink) !important; }
.sed-section { padding: 18px 0; border-bottom: 1px solid var(--line); }
.sed-section:last-child { border-bottom: none; }
.sed-h { font-size: 16px; font-weight: 800; margin: 0 0 12px; }
.sed-h-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.sed-h-aside { font-size: 13px; color: var(--muted); font-variant-numeric: tabular-nums; }
.sed-label { display: block; font-size: 13px; font-weight: 700; color: var(--ink-soft); margin: 4px 0 8px; }
.sed-body ::v-deep .v-input__slot { border-radius: 10px !important; }

.sed-note { display: flex; align-items: flex-start; gap: 6px; font-size: 13px; color: var(--muted); margin: 10px 0 0; line-height: 1.5; }
.sed-error-text { color: var(--danger); font-size: 13px; margin: 8px 0 0; }

/* audio */
.sed-audio { margin-top: 14px; }
.sed-audio__input { display: none; }
.sed-drop {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 22px 16px; border: 1.5px dashed #c3cde0; border-radius: 14px;
  background: #f7f9fc; cursor: pointer; text-align: center;
  transition: border-color .15s, background-color .15s;
}
.sed-drop:hover, .sed-drop.is-over { border-color: var(--ink); background: #eef2f9; }
.sed-drop.is-error { border-color: var(--danger); }
.sed-drop__title { font-weight: 700; font-size: 15px; }
.sed-drop__hint { font-size: 13px; color: var(--muted); }
.sed-drop:focus-within { outline: 2px solid var(--gold); outline-offset: 2px; }
.sed-file {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px; border: 1px solid var(--line); border-radius: 14px; background: #f7f9fc;
}
.sed-file__play { background: var(--ink); }
.sed-file__play .v-icon { color: #fff !important; }
.sed-file__text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.sed-file__name { font-weight: 700; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; direction: ltr; text-align: right; }
.sed-file__size { font-size: 12px; color: var(--muted); }

/* the keypad */
.sed-keypad {
  display: grid;
  grid-template-columns: repeat(3, 64px);
  gap: 10px;
  justify-content: end; /* right side of the RTL dialog */
  direction: ltr; /* keypad order like a real phone: 1 2 3 left to right */
}
.sed-keys-locked { display: flex; gap: 8px; flex-wrap: wrap; }
.sed-key--chip { width: 44px; height: 48px; display: inline-flex; align-items: center; justify-content: center; font-size: 19px; cursor: default; }
.sed-key {
  height: 54px; border-radius: 14px;
  border: 1px solid #cfd7e6; background: #fff;
  font: 800 22px 'Heebo', sans-serif; color: var(--ink);
  font-variant-numeric: tabular-nums;
  transition: background-color .12s, color .12s, border-color .12s, transform .08s;
}
.sed-key:hover:not(:disabled) { border-color: var(--ink); }
.sed-key:active:not(:disabled) { transform: scale(.96); }
.sed-key.is-on { background: var(--ink); border-color: var(--ink); color: #fff; }
.sed-key:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.sed-key--blank { border: none; background: transparent; }
.sed-key:disabled { cursor: not-allowed; }

/* rules */
.sed-rules { display: grid; gap: 2px; }
.sed-rule {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 10px 0;
}
.sed-rule--sub { padding: 2px 0 10px 0; padding-inline-start: 18px; border-inline-start: 2px solid var(--line); }
.sed-rule--flat { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--line); }
.sed-rule__text { display: flex; flex-direction: column; gap: 2px; }
.sed-rule__title { font-weight: 700; font-size: 15px; }
.sed-rule__hint { font-size: 13px; color: var(--muted); line-height: 1.45; }
.sed-switch { margin: 0 !important; padding: 0 !important; flex: none; }
.sed-stepper { display: inline-flex; align-items: center; gap: 4px; border: 1px solid var(--line); border-radius: 10px; padding: 2px; }
.sed-stepper__value { min-width: 28px; text-align: center; font-weight: 800; font-size: 16px; font-variant-numeric: tabular-nums; }

/* segmented choices */
.sed-seg { display: inline-flex; flex-wrap: wrap; background: #e9edf5; border-radius: 12px; padding: 4px; gap: 2px; margin-bottom: 12px; }
.sed-seg__btn { padding: 7px 14px; border-radius: 9px; font: 500 14px 'Heebo', sans-serif; color: var(--ink-soft); }
.sed-seg__btn.is-active { background: #fff; color: var(--ink); font-weight: 700; box-shadow: 0 1px 2px rgba(13, 44, 109, .12); }
.sed-seg__btn:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.sed-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.sed-duration { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; font-size: 15px; }
.sed-duration__input { max-width: 90px; }
.sed-preview { font-size: 14px; color: var(--ink-soft); margin: 4px 0 0; }

/* footer */
.sed-foot { border-top: 1px solid var(--line); padding: 12px 24px 16px; background: #fff; }
.sed-foot__error { display: flex; align-items: center; gap: 6px; color: var(--danger); font-size: 14px; margin: 0 0 10px; }
.sed-foot__actions { display: flex; justify-content: flex-end; gap: 8px; }
.sed-foot__actions .v-btn { border-radius: 10px !important; font-weight: 700 !important; letter-spacing: 0 !important; }
.sed-save { min-width: 140px; }

@media (max-width: 600px) {
  .sed-body { padding: 4px 16px 8px !important; }
  .sed-head, .sed-foot { padding-inline: 16px; }
  .sed-keypad { grid-template-columns: repeat(3, 1fr); justify-content: stretch; }
  .sed-row2 { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .sed-key, .sed-drop { transition: none; }
}
</style>